import { FormEvent, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { mfaChallenge } from "../api/auth";
import { useAuth } from "../stores/auth";

export default function MfaChallenge() {
  const navigate = useNavigate();
  const location = useLocation();
  const { challengeToken, from: rawFrom } =
    (location.state as { challengeToken?: string; from?: string } | null) ?? {};
  const from = safeRedirect(rawFrom);
  const setSession = useAuth((s) => s.setSession);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      if (!challengeToken) {
        navigate("/login");
        return;
      }
      const data = await mfaChallenge(challengeToken, code);
      setSession({
        accessToken: data.access_token,
        refreshToken: data.refresh_token,
        mfaRequired: data.mfa_required,
      });
      navigate(from);
    } catch (e) {
      setError(extractError(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-slate-50 p-6">
      <div className="w-full max-w-md rounded-2xl bg-white p-8 shadow-sm">
        <h1 className="text-2xl font-semibold text-slate-900">Extra security code</h1>
        <p className="mt-2 text-slate-600">
          Open your authenticator app and type the current 6-digit code.
        </p>

        <form onSubmit={onSubmit} className="mt-8 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700" htmlFor="code">
              6-digit code
            </label>
            <input
              id="code"
              type="text"
              inputMode="numeric"
              pattern="\d{6}"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              className="mt-1 w-full rounded-lg border border-slate-300 p-3 text-center text-2xl tracking-widest"
              required
              autoComplete="one-time-code"
              autoFocus
            />
          </div>
          {error ? (
            <p role="alert" className="rounded-lg bg-red-50 p-3 text-sm text-red-800">
              {error}
            </p>
          ) : null}
          <button
            type="submit"
            disabled={submitting || code.length !== 6}
            className="w-full rounded-lg bg-slate-900 p-3 font-medium text-white disabled:opacity-50"
          >
            {submitting ? "Checking…" : "Sign in"}
          </button>
        </form>
      </div>
    </main>
  );
}

function safeRedirect(path: string | undefined): string {
  if (path && path.startsWith("/") && !path.startsWith("//")) return path;
  return "/dashboard";
}

function extractError(e: unknown): string {
  if (typeof e === "object" && e && "response" in e) {
    const response = (e as { response?: { data?: { detail?: string } } }).response;
    if (response?.data?.detail) return response.data.detail;
  }
  return "That code didn't match. Try again.";
}
